'use client';

import React, { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { MotionConfig, motion } from 'motion/react';
import PostCommentForm from '@/components/(blog)/post/PostCommentForm';
import AverageStars from '@/components/(postTypes)/platforms/(archive)/layout/(review)/form/AverageStars';
import RatingStars from '@/components/(postTypes)/platforms/(archive)/layout/(review)/form/RatingStars';
import { PostRatings } from '@/types/blog/single';
import { toPersianDigits } from '@/utils/persianUtils';
import {
    CommentNode,
    CommentsData,
} from '@/components/(postTypes)/platforms/(archive)/layout/(review)/list/Reviews';

interface SubmitReviewProps {
    currentPostId: number;
    postRatings?: PostRatings;
    brokerName: string;
    commentCount: number;
    type: 'brokers' | 'exchanges' | 'props' | 'gold' | undefined;
    comments?: CommentNode[];
    hasNextPage?: CommentsData['comments']['pageInfo']['hasNextPage'];
}

const TYPE_LABEL: Record<NonNullable<SubmitReviewProps['type']>, string> = {
    brokers: 'بروکر',
    exchanges: 'صرافی',
    props: 'پراپ',
    gold: 'سایت طلا',
};

/** Bar color per star row (5 → green … 1 → red), same scale as the rating stars. */
const ROW_COLORS: Record<number, string> = {
    5: '#22c55e',
    4: '#84cc16',
    3: '#eab308',
    2: '#f97316',
    1: '#ef4444',
};

interface Stats {
    average: number;
    count: number;
    /** votes per star, index 0 = 1 star … index 4 = 5 stars */
    stars: number[];
}

const statsFrom = (postRatings?: PostRatings): Stats => {
    // relativity comes from the panel as [5★, 4★, 3★, 2★, 1★]
    const rows = postRatings?.relativity || [];
    const stars = [0, 1, 2, 3, 4].map((i) => Number(rows[4 - i]?.count) || 0);
    return {
        average: Number(postRatings?.average) || 0,
        count: Number(postRatings?.count) || 0,
        stars,
    };
};

const fmt = (n: number, digits = 0) => toPersianDigits(n.toFixed(digits));

const SubmitReview: React.FC<SubmitReviewProps> = ({
    currentPostId,
    postRatings,
    brokerName,
    commentCount,
    type,
    comments,
    hasNextPage,
}) => {
    const [stats, setStats] = useState<Stats>(() => statsFrom(postRatings));
    /** Bumped on each saved rating: the number pulses so the visitor sees it change. */
    const [bump, setBump] = useState(0);

    useEffect(() => {
        setStats(statsFrom(postRatings));
    }, [postRatings]);

    // The server returns the new average and count; the per-star bars are updated here the same
    // way the panel counts them (a changed vote moves from its old row to the new one).
    const handleRated = ({
        rating,
        previous,
        average,
        count,
    }: {
        rating: number;
        previous: number | null;
        average: number;
        count: number;
    }) => {
        setStats((old) => {
            // Platforms on the 30-day drip-feed plan show stored totals that the rating API may
            // not report; never let the shown numbers drop because of that.
            const serverOk = count >= old.count;
            const isNewVote = serverOk ? count > old.count : previous === null;
            const stars = [...old.stars];
            if (!isNewVote && previous && previous !== rating) {
                stars[previous - 1] = Math.max(0, stars[previous - 1] - 1);
            }
            if (isNewVote || (previous && previous !== rating)) stars[rating - 1] += 1;
            if (serverOk) return { average: average || old.average, count, stars };
            const localCount = old.count + (isNewVote ? 1 : 0);
            const sum = old.average * old.count - (isNewVote ? 0 : previous || 0) + rating;
            return { average: localCount ? sum / localCount : rating, count: localCount, stars };
        });
        setBump((n) => n + 1);
    };

    const total = Math.max(
        stats.count,
        stats.stars.reduce((a, b) => a + b, 0),
    );
    const label = TYPE_LABEL[type || 'exchanges'];

    return (
        <MotionConfig reducedMotion='user'>
            <section className={'flex flex-col justify-between'}>
                <div className='mb-6 grid gap-5 rounded-2xl border border-black/5 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5 md:grid-cols-[1fr_auto_1fr] md:items-center lg:p-6'>
                    {/* Rate */}
                    <div className='flex flex-col items-center gap-2 md:items-start'>
                        <h3 className={'text-lg font-semibold text-center md:text-right'}>
                            شما به {brokerName} چه امتیازی می‌دید؟
                        </h3>
                        <RatingStars currentPostId={currentPostId} onRating={handleRated} />
                    </div>

                    {/* Average */}
                    <div className='flex flex-col items-center gap-1 border-y border-black/5 py-4 dark:border-white/10 md:border-x md:border-y-0 md:px-8 md:py-0'>
                        <motion.div
                            key={`avg-${bump}`}
                            initial={bump ? { scale: 1.25, opacity: 0.6 } : false}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 18 }}
                            className='text-4xl font-bold leading-none'
                        >
                            {fmt(stats.average, 1)}
                        </motion.div>
                        <AverageStars value={stats.average} />
                        <div className='text-xs text-gray-500 dark:text-white/60'>
                            از{' '}
                            <motion.span
                                key={`count-${bump}`}
                                initial={bump ? { color: '#22c55e' } : false}
                                animate={{ color: 'currentColor' }}
                                transition={{ duration: 1.2 }}
                                className='font-semibold'
                            >
                                {fmt(total)}
                            </motion.span>{' '}
                            رأی
                        </div>
                    </div>

                    {/* Distribution */}
                    <ul className='flex flex-col gap-1.5' aria-label='پراکندگی امتیازها'>
                        {[5, 4, 3, 2, 1].map((star) => {
                            const votes = stats.stars[star - 1];
                            const ptg = total > 0 ? (votes / total) * 100 : 0;
                            return (
                                <li
                                    key={star}
                                    className='flex items-center gap-2 text-xs text-gray-600 dark:text-white/70'
                                >
                                    <span className='flex w-7 shrink-0 items-center gap-0.5'>
                                        {fmt(star)}
                                        <Star className='size-3 fill-yellow-400 stroke-yellow-400' />
                                    </span>
                                    <span
                                        className='relative h-2 flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-white/10'
                                        role='progressbar'
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                        aria-valuenow={Math.round(ptg)}
                                        aria-label={`${star} ستاره`}
                                    >
                                        <motion.span
                                            className='absolute inset-y-0 right-0 rounded-full'
                                            style={{ backgroundColor: ROW_COLORS[star] }}
                                            initial={false}
                                            animate={{ width: `${ptg}%` }}
                                            transition={{
                                                type: 'spring',
                                                stiffness: 120,
                                                damping: 20,
                                            }}
                                        />
                                    </span>
                                    <span className='w-8 shrink-0 text-left tabular-nums'>
                                        {fmt(votes)}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <PostCommentForm
                    postId={currentPostId}
                    commentCount={commentCount}
                    customPlaceholder={`نظر خود را در رابطه با این ${label} با ما به اشتراک بگذارید.`}
                    comments={comments}
                    hasNextPage={hasNextPage}
                />
            </section>
        </MotionConfig>
    );
};

export default SubmitReview;
